import dayjs from 'dayjs'
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bar, BarChart as RechartsBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TimeFrame } from 'types/analytics'
import { formatTimeframeLabel, TIMEFRAME_OPTIONS } from 'utils/analytics'
import { ChartSeriesPoint } from 'utils/analytics/analyticsChartSeries'

import ChartSummary from './Summary'
import TimeframeButton from './TimeframeButton'
import ZoomControls from './ZoomControls'

const CHART_MARGINS = { top: 5, right: 5, left: 5, bottom: 5 }
const PIXELS_PER_POINT = 1.8

const calculatePercentChange = (previous?: number, current?: number) => {
  if (previous === undefined || current === undefined) return undefined
  if (!Number.isFinite(previous) || !Number.isFinite(current) || previous === 0) return undefined
  return ((current - previous) / Math.abs(previous)) * 100
}

interface BarSummaryConfig {
  title: string
  timeframeLabel?: string
  changePercent?: number
  isPrice?: boolean
  valueFormatter?: (value?: number) => string
}

export interface BarChartProps {
  data: any[]
  color?: string
  height?: number
  minHeight?: number
  isAnimationActive?: boolean
  className?: string
  summaryProps: BarSummaryConfig
  defaultValue?: number
  defaultLabel?: string
  topRight?: ReactNode | undefined
  bottomLeft?: ReactNode | undefined
  bottomRight?: ReactNode | undefined
  showTimeSelector?: boolean
  selectedTimeframe?: string
  onTimeframeChange?: (timeframe: string) => void
  onLatestPointVisibleChange?: (visible: boolean) => void
  yAxisTickFormatter?: (value: number) => string
  showZoomControls?: boolean
  resetKey?: string
}

function BarChart({
  data,
  color = '#3083dc',
  height = 300,
  minHeight,
  isAnimationActive = false,
  className,
  summaryProps,
  defaultValue,
  defaultLabel,
  topRight,
  bottomLeft,
  bottomRight,
  showTimeSelector = false,
  selectedTimeframe = 'D',
  onTimeframeChange,
  onLatestPointVisibleChange,
  yAxisTickFormatter,
  showZoomControls = true,
  resetKey,
}: BarChartProps) {
  const lastHoverIndex = useRef<number | null>(null)
  const hoverStateRef = useRef<{ index: number | null; value?: number; label?: string; change?: number }>({
    index: null,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(400)
  const [hoverPoint, setHoverPoint] = useState<{ value?: number; label?: string } | null>(null)
  const [isLatestVisible, setIsLatestVisible] = useState(true)
  const [hoverChangePercent, setHoverChangePercent] = useState<number | undefined>(undefined)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry?.contentRect) {
        setContainerWidth(entry.contentRect.width)
      }
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const processedData = useMemo<ChartSeriesPoint[]>(() => {
    if (!Array.isArray(data) || data.length === 0) return []

    const typed = data as ChartSeriesPoint[]
    let needsCopy = false
    for (const point of typed) {
      if (!point || typeof point.t !== 'number') {
        needsCopy = true
        break
      }
    }

    if (!needsCopy) {
      return typed
    }

    return (data as Array<{ time: string; value: number; label?: string; t?: number; index?: number }>)
      .filter(Boolean)
      .map((point, index) => ({
        time: point.time,
        value: point.value,
        label: point.label ?? dayjs(point.time).format('MMM D, YYYY'),
        t: typeof point.t === 'number' ? point.t : +new Date(point.time),
        index: typeof point.index === 'number' ? point.index : index,
      }))
  }, [data])

  const totalCount = processedData.length
  const ZOOM_STEP_RATIO = 0.3
  const PAN_STEP_RATIO = 0.4

  const getInitialMaxWindow = () => {
    const maxPoints = Math.floor(containerWidth / PIXELS_PER_POINT)
    return Math.max(10, Math.min(maxPoints, totalCount))
  }

  const rangeMetaRef = useRef<{ count: number }>({ count: totalCount })

  const [visibleRange, setVisibleRange] = useState(() => {
    if (totalCount === 0) return { start: 0, end: 0 }
    const initialWindow = getInitialMaxWindow()
    const start = Math.max(0, totalCount - initialWindow)
    return { start, end: totalCount - 1 }
  })

  const effectiveRange = useMemo(() => {
    const maxIndex = totalCount > 0 ? totalCount - 1 : 0
    if (selectedTimeframe !== 'D') {
      return { start: 0, end: maxIndex }
    }

    const clampedStart = Math.max(0, Math.min(visibleRange.start, maxIndex))
    const clampedEnd = Math.max(clampedStart, Math.min(visibleRange.end, maxIndex))
    const previousCount = rangeMetaRef.current.count
    const wasPreviouslyFull =
      previousCount <= 1 || (visibleRange.start === 0 && visibleRange.end === Math.max(0, previousCount - 1))
    const shouldShowFullRange = totalCount > 1 && previousCount !== totalCount && wasPreviouslyFull

    if (shouldShowFullRange) {
      return { start: 0, end: maxIndex }
    }

    return { start: clampedStart, end: clampedEnd }
  }, [selectedTimeframe, totalCount, visibleRange.end, visibleRange.start])

  useEffect(() => {
    const prevCount = rangeMetaRef.current.count

    setVisibleRange((prevRange) => {
      const endIndex = totalCount > 0 ? totalCount - 1 : 0

      if (totalCount === 0) {
        rangeMetaRef.current.count = totalCount
        return prevRange.start === 0 && prevRange.end === 0 ? prevRange : { start: 0, end: 0 }
      }

      const prevFullRange = prevCount <= 1 || (prevRange.start === 0 && prevRange.end === prevCount - 1)

      let nextStart: number
      let nextEnd: number

      if (prevFullRange) {
        nextStart = 0
        nextEnd = endIndex
      } else {
        const denom = Math.max(prevCount - 1, 1)
        const startRatio = prevRange.start / denom
        const endRatio = prevRange.end / denom
        nextStart = Math.max(0, Math.min(endIndex, Math.round(startRatio * endIndex)))
        nextEnd = Math.max(nextStart, Math.min(endIndex, Math.round(endRatio * endIndex)))
      }

      rangeMetaRef.current.count = totalCount

      if (prevRange.start === nextStart && prevRange.end === nextEnd) {
        return prevRange
      }

      return { start: nextStart, end: nextEnd }
    })
  }, [selectedTimeframe, totalCount])

  // Recalculate visible range when container width changes to ensure correct initial zoom
  useEffect(() => {
    if (totalCount === 0) return

    setVisibleRange((prevRange) => {
      const maxWindow = Math.max(10, Math.min(Math.floor(containerWidth / PIXELS_PER_POINT), totalCount))
      const currentWindow = prevRange.end - prevRange.start + 1

      const isAtMaxZoom = currentWindow >= totalCount * 0.9

      if (isAtMaxZoom) {
        const start = Math.max(0, totalCount - maxWindow)
        return { start, end: totalCount - 1 }
      }

      return prevRange
    })
  }, [containerWidth, totalCount])

  const chartData = useMemo(() => {
    if (selectedTimeframe !== 'D') return processedData
    if (!totalCount) return []

    const windowed = processedData.slice(effectiveRange.start, effectiveRange.end + 1)

    return windowed
  }, [effectiveRange, processedData, selectedTimeframe, totalCount])
  const chartDataPositionLookup = useMemo(() => {
    const lookup = new Map<number, number>()
    chartData.forEach((point, position) => {
      if (typeof point.index === 'number') {
        lookup.set(point.index, position)
      }
    })
    return lookup
  }, [chartData])

  const showsLatestPoint = useMemo(() => {
    if (!processedData.length || !chartData.length) return false
    const lastProcessed = processedData[processedData.length - 1]
    const lastVisible = chartData[chartData.length - 1]
    return dayjs(lastProcessed.time).isSame(lastVisible.time)
  }, [processedData, chartData])

  const defaultPoint = useMemo(() => {
    if (!chartData.length) return undefined
    return chartData[chartData.length - 1]
  }, [chartData])

  const formatLabel = useCallback(
    (point?: ChartSeriesPoint) => {
      if (!point) return undefined
      if (selectedTimeframe === 'D') return point.label
      const timeframe = selectedTimeframe as TimeFrame
      const isLatest = processedData.length > 0 && processedData[processedData.length - 1] === point
      return formatTimeframeLabel(dayjs(point.t), timeframe, {
        isLatest,
        now: dayjs(),
      })
    },
    [processedData, selectedTimeframe]
  )

  const fallbackLabel = useMemo(() => {
    if (defaultLabel !== undefined) return defaultLabel
    if (defaultPoint && selectedTimeframe !== 'D') {
      return formatLabel(defaultPoint)
    }
    return undefined
  }, [defaultLabel, defaultPoint, formatLabel, selectedTimeframe])

  const fallbackValue = useMemo(() => {
    if (selectedTimeframe === 'D' && typeof defaultValue === 'number') return defaultValue
    return defaultPoint?.value
  }, [defaultPoint?.value, defaultValue, selectedTimeframe])

  useEffect(() => {
    lastHoverIndex.current = null
    setHoverPoint(null)
    setHoverChangePercent(undefined)
    hoverStateRef.current = { index: null }
  }, [fallbackValue, fallbackLabel, resetKey])

  const xAxisFormatter = useCallback(
    (time: number) => dayjs(time).format(selectedTimeframe === 'M' ? 'MMM' : 'MMM DD'),
    [selectedTimeframe]
  )

  const defaultYAxisFormatter = useCallback((value: number) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(0)}B`
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`
    return `$${value.toFixed(0)}`
  }, [])

  const resolvedYAxisFormatter = useMemo(
    () => yAxisTickFormatter ?? defaultYAxisFormatter,
    [yAxisTickFormatter, defaultYAxisFormatter]
  )

  const handleMouseLeave = useCallback(() => {
    lastHoverIndex.current = null
    setHoverPoint(null)
    setHoverChangePercent(undefined)
    hoverStateRef.current = { index: null }
  }, [])

  const timeSelector = useMemo(() => {
    if (!showTimeSelector) return null
    return (
      <div className="flex gap-1">
        {TIMEFRAME_OPTIONS.map(
          (timeframe) =>
            onTimeframeChange && (
              <TimeframeButton
                key={timeframe}
                value={timeframe}
                onChange={onTimeframeChange}
                isSelected={selectedTimeframe === timeframe}
              />
            )
        )}
      </div>
    )
  }, [showTimeSelector, selectedTimeframe, onTimeframeChange])

  useEffect(() => {
    onLatestPointVisibleChange?.(showsLatestPoint)
    setIsLatestVisible(showsLatestPoint || selectedTimeframe !== 'D')
  }, [showsLatestPoint, selectedTimeframe, onLatestPointVisibleChange])

  const { minWindow, maxWindow } = useMemo(() => {
    const maxPoints = Math.floor(containerWidth / PIXELS_PER_POINT)
    const max = Math.max(10, Math.min(maxPoints, totalCount))

    const min = Math.max(10, Math.min(Math.floor(max / 4), totalCount))

    return { minWindow: min, maxWindow: max }
  }, [totalCount, containerWidth])

  const zoomControls =
    showZoomControls && selectedTimeframe === 'D' && totalCount > 0 ? (
      <ZoomControls
        totalCount={totalCount}
        visibleRange={effectiveRange}
        setVisibleRange={setVisibleRange}
        minWindow={minWindow}
        maxWindow={maxWindow}
        zoomRatio={ZOOM_STEP_RATIO}
        panRatio={PAN_STEP_RATIO}
      />
    ) : null

  const containerStyle = useMemo(() => {
    const base: React.CSSProperties = {
      transform: 'translateZ(0)',
      willChange: 'transform',
    }
    if (typeof minHeight === 'number') {
      base.minHeight = `${minHeight}px`
    }
    return base
  }, [minHeight])

  const chartHeight = height ?? 300

  const handleMouseMove = useCallback(
    (state: any | undefined) => {
      const payload = state?.activePayload?.[0]?.payload as ChartSeriesPoint | undefined
      if (!payload) return

      const payloadIndex = typeof payload.index === 'number' ? payload.index : null
      const lookupPosition = payloadIndex !== null ? chartDataPositionLookup.get(payloadIndex) : undefined
      let pointPosition =
        lookupPosition !== undefined ? lookupPosition : chartData.findIndex((point) => point === payload)
      if (pointPosition < 0 && payloadIndex !== null) {
        pointPosition = chartData.findIndex((point) => point.index === payloadIndex)
      }
      if (pointPosition < 0) {
        return
      }

      const formattedLabel = formatLabel(payload) ?? defaultLabel
      const previousPoint = pointPosition > 0 ? chartData[pointPosition - 1] : undefined
      const change = pointPosition > 0 ? calculatePercentChange(previousPoint?.value, payload.value) : undefined

      const nextHoverState = {
        index: payloadIndex ?? chartData[pointPosition]?.index ?? null,
        value: payload.value,
        label: formattedLabel,
        change,
      }

      const { index, value, label, change: prevChange } = hoverStateRef.current
      if (
        index === nextHoverState.index &&
        value === nextHoverState.value &&
        label === nextHoverState.label &&
        prevChange === change
      ) {
        return
      }

      hoverStateRef.current = nextHoverState
      lastHoverIndex.current = nextHoverState.index
      setHoverPoint({ value: payload.value, label: formattedLabel })
      setHoverChangePercent(change)
    },
    [chartData, chartDataPositionLookup, defaultLabel, formatLabel]
  )

  const timeframeKey = (selectedTimeframe as TimeFrame) ?? 'D'
  const timeframeChangePercent = useMemo(() => {
    if (chartData.length < 2) return undefined
    const latest = chartData[chartData.length - 1]?.value
    const previous = chartData[chartData.length - 2]?.value
    return calculatePercentChange(previous, latest)
  }, [chartData])
  const shouldShowBaseChange = timeframeKey !== 'D' || isLatestVisible
  const baseChangeValue = shouldShowBaseChange ? timeframeChangePercent : undefined

  const summary = useMemo(() => {
    const { title, timeframeLabel, isPrice, valueFormatter } = summaryProps
    const summaryValue = hoverPoint?.value ?? fallbackValue
    const summaryLabel = hoverPoint?.label ?? fallbackLabel
    return (
      <ChartSummary
        title={title}
        timeframe={timeframeLabel}
        value={summaryValue}
        label={summaryLabel}
        changePercent={baseChangeValue}
        hoverChangePercent={hoverChangePercent}
        isPrice={isPrice}
        valueFormatter={valueFormatter}
        showChange
      />
    )
  }, [summaryProps, hoverPoint, fallbackValue, fallbackLabel, baseChangeValue, hoverChangePercent])

  return (
    <div
      ref={containerRef}
      className={`w-full min-w-0 p-4 flex flex-col  bg-gray-900 rounded-lg border  border-gray-700 [&>*]:text-base flex-shrink-0 ${
        className ?? ''
      }`.trim()}
      style={containerStyle}
    >
      <div className="flex justify-between items-start mb-3">
        <div>{summary}</div>
        <div className="flex flex-col gap-3 items-end">
          <div className="flex flex-col gap-2 items-end">
            {timeSelector}
            {zoomControls}
          </div>
          <div>{topRight ?? null}</div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <RechartsBarChart
          data={chartData}
          margin={CHART_MARGINS}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        >
          <XAxis
            dataKey="t"
            axisLine={false}
            tickLine={false}
            tickFormatter={xAxisFormatter}
            minTickGap={10}
            allowDataOverflow={false}
            tick={{ fontSize: 12, fill: '#888' }}
            tickMargin={12}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={resolvedYAxisFormatter}
            width={56}
            allowDataOverflow={false}
            tick={{ fontSize: 12, fill: '#888' }}
          />
          <Tooltip
            cursor={{ fill: 'rgba(134, 178, 164, 0.1)' }}
            contentStyle={{ display: 'none' }}
            isAnimationActive={false}
            animationDuration={0}
          />
          <Bar
            dataKey="value"
            fill={color}
            isAnimationActive={isAnimationActive}
            animationDuration={0}
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
        </RechartsBarChart>
      </ResponsiveContainer>

      <div className="flex justify-between items-center mt-2 text-sm text-gray-400">
        <div>{bottomLeft ?? null}</div>
        <div className="flex items-center gap-2">{bottomRight ?? null}</div>
      </div>
    </div>
  )
}

export default React.memo(BarChart)
